import { serverIP } from '../api';

class PushNotificationManager {
  constructor() {
    this.swRegistration = null;
    this.vapidPublicKey = null; // Will be set from backend

    // Enhanced detection for better iOS Safari support
    this.isSupported = this.detectPushSupport();
  }

  // Enhanced push notification support detection
  detectPushSupport() {
    // Check basic requirements
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;

    // Debug information
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    // PWA detection
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');

    const isIOSPWA = isIOS && isPWA;

    console.log('Push notification detection:', {
      userAgent: userAgent,
      isIOS: isIOS,
      isSafari: isSafari,
      isPWA: isPWA,
      isIOSPWA: isIOSPWA,
      serviceWorker: hasServiceWorker,
      pushManager: hasPushManager,
      notification: hasNotification,
      displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
      standalone: window.navigator.standalone,
      pushManagerProto: typeof PushManager !== 'undefined' ? Object.getOwnPropertyNames(PushManager.prototype) : 'undefined'
    });

    if (!hasServiceWorker || !hasPushManager || !hasNotification) {
      console.log('❌ Basic push requirements not met');
      return false;
    }

    // Additional checks for iOS Safari/PWA
    if (isIOS && (isSafari || isPWA)) {
      // iOS Safari/PWA supports push notifications since iOS 16.4
      // Check if we can access PushManager.prototype.subscribe
      try {
        const hasSubscribe = typeof PushManager !== 'undefined' &&
          typeof PushManager.prototype.subscribe === 'function';

        console.log(`✅ iOS ${isPWA ? 'PWA' : 'Safari'} push support detected:`, hasSubscribe);

        // For iOS PWA, be more permissive - if basic APIs exist, assume support
        if (isPWA && hasServiceWorker && hasPushManager && hasNotification) {
          console.log('🔧 iOS PWA: Assuming push support based on API availability');
          return true;
        }

        return hasSubscribe;
      } catch (e) {
        console.warn(`❌ iOS ${isPWA ? 'PWA' : 'Safari'} push detection failed:`, e);

        // For iOS PWA, even if detection fails, try anyway if basic APIs exist
        if (isPWA && hasServiceWorker && hasPushManager && hasNotification) {
          console.log('🔧 iOS PWA: Detection failed but basic APIs exist, assuming support');
          return true;
        }

        return false;
      }
    }

    console.log('✅ Push notifications supported');
    return true;
  }

  // Check if push notifications are supported
  isNotificationSupported() {
    return this.isSupported;
  }

  // Get current notification permission status
  getPermissionStatus() {
    if (!this.isNotificationSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission(forceAttempt = false) {
    if (!forceAttempt && !this.isNotificationSupported()) {
      throw new Error('Push notifications are not supported');
    }

    // Check if permission is already granted
    const currentPermission = Notification.permission;
    if (currentPermission !== 'default') {
      console.log('📋 Permission already determined:', currentPermission);
      return currentPermission;
    }

    // iOS PWA specific handling
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS && isPWA) {
      console.log('🔄 iOS PWA: Requesting permission with timeout...');

      // Add timeout for iOS PWA permission request
      const permissionPromise = Notification.requestPermission();
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => {
          console.log('⏰ Permission request timeout, checking current status...');
          resolve(Notification.permission);
        }, 5000)
      );

      const permission = await Promise.race([permissionPromise, timeoutPromise]);
      console.log('📋 iOS PWA Permission result:', permission);
      return permission;
    } else {
      // Regular permission request
      const permission = await Notification.requestPermission();
      console.log('📋 Permission result:', permission);
      return permission;
    }
  }

  // Initialize service worker and push manager
  async initialize() {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported');
      return false;
    }

    try {
      // PWA-specific service worker registration
      const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;

      console.log('🔧 Initializing service worker...', { isPWA });

      // Register service worker with PWA-optimized settings
      this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // PWA optimization
      });

      console.log('📋 Service Worker registered:', this.swRegistration);

      // PWA-specific: Don't force updates to prevent refresh loops
      if (isPWA && this.swRegistration.waiting) {
        console.log('⚠️ PWA: Service worker update waiting, but not forcing to prevent refresh loops');
        // this.swRegistration.waiting.postMessage({ action: 'skipWaiting' });
      }

      // Wait for service worker to be ready and active
      await this.waitForServiceWorkerReady();

      // Get VAPID public key from backend
      await this.getVapidKey();

      return true;
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
      return false;
    }
  }

  // Wait for service worker to be ready and active
  async waitForServiceWorkerReady() {
    if (!this.swRegistration) {
      throw new Error('Service Worker not registered');
    }

    // If service worker is already active, return immediately
    if (this.swRegistration.active) {
      console.log('✅ Service Worker already active');
      return this.swRegistration;
    }

    // PWA-specific: Multiple attempts for service worker readiness
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (isPWA) {
      console.log('🔄 PWA mode: Waiting for service worker with timeout...');

      // Wait with timeout for PWA
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Service Worker timeout in PWA mode')), 10000)
      );

      const readyPromise = navigator.serviceWorker.ready;

      try {
        const registration = await Promise.race([readyPromise, timeoutPromise]);
        console.log('✅ PWA Service Worker ready:', registration);
        this.swRegistration = registration;
        return registration;
      } catch (error) {
        console.warn('⚠️ PWA Service Worker timeout, trying alternative approach...');

        // Fallback: Try to get existing registration
        const existingRegistration = await navigator.serviceWorker.getRegistration('/');
        if (existingRegistration && existingRegistration.active) {
          console.log('✅ Found existing active service worker');
          this.swRegistration = existingRegistration;
          return existingRegistration;
        }

        throw new Error('PWA Service Worker failed to become ready');
      }
    } else {
      // Regular browser mode
      const registration = await navigator.serviceWorker.ready;
      console.log('✅ Service Worker ready:', registration);
      this.swRegistration = registration;
      return registration;
    }
  }

  // Get VAPID public key from backend
  async getVapidKey() {
    try {
      const response = await fetch(`${serverIP}api/notifications/vapid-key`);
      if (response.ok) {
        const data = await response.json();
        this.vapidPublicKey = data.publicKey;
      }
    } catch (error) {
      console.error('Failed to get VAPID key:', error);
      // Use a default key for development (should be replaced with actual key)
      this.vapidPublicKey = 'BAO-yEpWnRdqy5r8kqzFQ8ghejSqMDfrY38g9fE2QMJJzT2I6MmPfF5X55InxKLm4MbFIlsdSfJaIdnpcHmbRaw';
    }
  }

  // Convert VAPID key from base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Subscribe user to push notifications
  async subscribe(token, userId) {
    try {
      // Ensure service worker is initialized and ready
      if (!this.swRegistration) {
        console.log('🔄 Initializing service worker for subscription...');
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize service worker');
        }
      }

      // Double-check that service worker is active
      await this.waitForServiceWorkerReady();

      if (!this.vapidPublicKey) {
        await this.getVapidKey();
      }

      console.log('🔔 Attempting to subscribe to push notifications...', {
        hasRegistration: !!this.swRegistration,
        isActive: !!this.swRegistration?.active,
        hasVapidKey: !!this.vapidPublicKey
      });

      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      console.log('✅ Push Manager subscription successful:', subscription);

      // Send subscription to backend
      const response = await fetch(`${serverIP}api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          userId: userId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription to server');
      }

      console.log('✅ Push notification subscription saved to server');
      return subscription;
    } catch (error) {
      console.error('❌ Push subscription failed:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(token, userId) {
    if (!this.swRegistration) {
      return;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove subscription from backend
        await fetch(`${serverIP}api/notifications/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: userId
          })
        });

        console.log('Push notification unsubscription successful');
      }
    } catch (error) {
      console.error('Push unsubscription failed:', error);
      throw error;
    }
  }

  // Check if user is currently subscribed
  async isSubscribed() {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      return !!subscription;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  // Show a local notification (fallback for immediate notifications)
  showLocalNotification(title, options = {}) {
    if (!this.isNotificationSupported()) {
      console.warn('Notifications not supported');
      return;
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        vibrate: [200, 100, 200],
        ...options
      });

      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    }
  }

  // Setup automatic subscription on app load
  async setupAutoSubscription(token, userId) {
    try {
      // Initialize service worker
      const initialized = await this.initialize();
      if (!initialized) return false;

      // Check permission
      const permission = this.getPermissionStatus();

      if (permission === 'granted') {
        // Check if already subscribed
        const isSubscribed = await this.isSubscribed();

        if (!isSubscribed) {
          // Subscribe automatically if permission is already granted
          await this.subscribe(token, userId);
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Auto subscription setup failed:', error);
      return false;
    }
  }

  // PWA-specific setup method
  async setupPWANotifications(token, userId) {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (!isPWA || !isIOS) {
      console.log('Not iOS PWA, using standard setup');
      return this.setupAutoSubscription(token, userId);
    }

    console.log('🔧 Setting up PWA notifications for iOS...');

    try {
      // Step 1: Check current permission without requesting
      const currentPermission = Notification.permission;
      console.log('📋 Current PWA permission:', currentPermission);

      // Step 2: Initialize service worker with PWA optimizations
      const initialized = await this.initialize();
      if (!initialized) {
        console.warn('❌ PWA: Service worker initialization failed');
        return false;
      }

      // Step 3: If permission already granted, attempt subscription
      if (currentPermission === 'granted') {
        console.log('✅ PWA: Permission already granted, attempting subscription...');

        // Check if already subscribed
        const isSubscribed = await this.isSubscribed();
        if (!isSubscribed) {
          await this.subscribe(token, userId);
          console.log('✅ PWA: Successfully subscribed to notifications');
          return true;
        } else {
          console.log('✅ PWA: Already subscribed to notifications');
          return true;
        }
      } else {
        console.log('⚠️ PWA: Permission not granted, user needs to enable manually');
        return false;
      }

    } catch (error) {
      console.error('❌ PWA notification setup failed:', error);
      return false;
    }
  }
}

// Create singleton instance
const pushManager = new PushNotificationManager();

export default pushManager;

// Export utility functions
export const requestNotificationPermission = (forceAttempt = false) => pushManager.requestPermission(forceAttempt);
export const subscribeToNotifications = (token, userId) => pushManager.subscribe(token, userId);
export const unsubscribeFromNotifications = (token, userId) => pushManager.unsubscribe(token, userId);
export const isNotificationSupported = () => pushManager.isNotificationSupported();
export const getNotificationPermission = () => pushManager.getPermissionStatus();
export const showLocalNotification = (title, options) => pushManager.showLocalNotification(title, options);
export const setupAutoSubscription = (token, userId) => pushManager.setupAutoSubscription(token, userId);
export const setupPWANotifications = (token, userId) => pushManager.setupPWANotifications(token, userId);
